import type { ChangeEvent, ComponentType } from 'react'
import { NumericFormat, PatternFormat } from 'react-number-format'
import { cn, Input, type InputProps } from '@nwwp/ui-shadcn'

type NumberInputProps = Omit<InputProps, 'type' | 'inputMode' | 'onChange' | 'value' | 'defaultValue'> & {
  value?: Exclude<InputProps['value'], readonly string[]>
  defaultValue?: Exclude<InputProps['defaultValue'], readonly string[]>
  mask?: 'numeric' | 'phone'
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
}

function NumberInput({ onChange, name, ref, mask = 'numeric', error, prefix, className, ...props }: NumberInputProps) {
  const handleValueChange = ({ value }: { value: string }) => {
    onChange?.({ target: { name: name ?? '', value } } as ChangeEvent<HTMLInputElement>)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomInput = Input as ComponentType<any>

  const fwdProps = {
    'aria-invalid': error ? (true as const) : undefined,
    name,
    // pl-7 must be passed here since we render the prefix icon ourselves —
    // NumericFormat strips the `prefix` prop before forwarding to customInput
    className: cn(prefix && 'pl-7', className),
    ...props,
  }

  const inputEl =
    mask === 'phone' ? (
      <PatternFormat
        format="(###) ###-####"
        customInput={CustomInput}
        getInputRef={ref}
        onValueChange={handleValueChange}
        inputMode="tel"
        {...fwdProps}
      />
    ) : (
      <NumericFormat
        thousandSeparator=","
        customInput={CustomInput}
        getInputRef={ref}
        onValueChange={handleValueChange}
        inputMode="numeric"
        {...fwdProps}
      />
    )

  return (
    <div>
      {prefix ? (
        <div className="relative">
          <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-darker select-none">
            {prefix}
          </span>
          {inputEl}
        </div>
      ) : (
        inputEl
      )}
      {error && <p className="mt-2 text-subtitle text-red">{error}</p>}
    </div>
  )
}

export { NumberInput }
export type { NumberInputProps }
